/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import {
  getTranslatedText,
  useIntl,
  Loader,
  useCurrencyCallback
} from '@nile/components';
import { MinimalDropDown } from '@nile/client_management/src/Containers/ClientDetails/ClientContractNew/ContractForm/Components';
import { AccountClient, GET_RETAINER_UTILIZATION_METRICS } from '@nile/graphql';

const ReactECharts = React.lazy(() => import('echarts-for-react'));

const ContractsUtilizationChart = ({ data }: { data: any }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const intl = useIntl();
  const [chartData, setChartData] = useState<any>({
    title: {
      text: 'Top 10 Utilization contracts'
    },
    tooltip: {},
    legend: {
      data: ['Utilization']
    },
    xAxis: {
      data: [
        'Contract 1',
        'Contract 2',
        'Contract 3',
        'Contract 4',
        'Contract 5',
        'Contract 6'
      ]
    },
    yAxis: {},
    series: [
      {
        name: 'Utilization',
        type: 'bar',
        data: [5, 20, 36, 10, 10, 20]
      }
    ]
  });

  const usageOptions = [
    {
      label: getTranslatedText(intl, 'invoice.retainerReport.highest.usage'),
      value: 'HIGHEST_USAGE',
      condition: {
        joinOperator: 'AND',
        operands: [
          {
            attribute: 'utilization',
            operator: 'between',
            value: ['70', '100']
          },
          {
            value: 'ACTIVE',
            operator: 'is',
            attribute: 'clientContractInfo.contractStatus'
          }
        ]
      },
      sort: [
        {
          attribute: 'utilization',
          order: 'DESC'
        }
      ]
    },
    {
      label: getTranslatedText(intl, 'invoice.retainerReport.lowest.usage'),
      value: 'LOWEST_USAGE',
      condition: {
        joinOperator: 'AND',
        operands: [
          {
            attribute: 'utilization',
            operator: 'between',
            value: ['30', '70']
          },
          {
            value: 'ACTIVE',
            operator: 'is',
            attribute: 'clientContractInfo.contractStatus'
          }
        ]
      },
      sort: [
        {
          attribute: 'utilization',
          order: 'ASC'
        }
      ]
    }
  ];

  const [selectedUsage, setSelectedUsage] = useState<{
    label: string;
    value: string;
    condition: any;
    sort: {
      attribute: string;
      order: string;
    }[];
  }>(usageOptions?.[0]);

  const handleUsageChange = (option: {
    label: string;
    value: string;
    condition: any;
    sort: {
      attribute: string;
      order: string;
    }[];
  }) => {
    setSelectedUsage(option);
  };

  function fetchChartData() {
    setLoading(true);
    AccountClient.query({
      query: GET_RETAINER_UTILIZATION_METRICS,
      variables: {
        input: {
          condition: selectedUsage?.condition,
          sort: selectedUsage?.sort,
          page: 1,
          pageSize: 10
        }
      }
    })
      .then((response: any) => {
        setChartData({
          retainers:
            response.data.getRetainerUtilizationMetrics?.retainerUtilizations,
          xAxis: {
            data: response.data.getRetainerUtilizationMetrics?.retainerUtilizations.map(
              (item: any) => item.contractName
            )
          },
          series: [
            {
              name: 'Utilization',
              type: 'bar',
              data: response.data.getRetainerUtilizationMetrics?.retainerUtilizations.map(
                (item: any) => item.utilization
              )
            }
          ]
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    fetchChartData();
  }, [selectedUsage?.condition]);

  const { formatCurrency } = useCurrencyCallback();

  if (loading) {
    return (
      <div className="flex-center full-height min-height-300">
        <Loader />
      </div>
    );
  }

  const option = {
    title: {
      text: getTranslatedText(intl, 'invoice.retainerReport.chart.title')
    },
    color: 'rgba(61, 79, 240, 1)',
    grid: {
      top: '20%'
    },
    tooltip: {
      trigger: 'item',
      formatter: (val) => {
        const { dataIndex } = val;
        const retainer = chartData?.retainers[dataIndex];
        const tpvalue = [
          {
            key: 'totalAllotted',
            label: getTranslatedText(intl, 'common.label.totalAllotted')
          },
          {
            key: 'consumed',
            label: getTranslatedText(intl, 'common.label.consumed')
          },
          {
            key: 'remaining',
            label: getTranslatedText(intl, 'common.label.remaining')
          },
          {
            key: 'utilization',
            label: getTranslatedText(intl, 'common.label.utilization')
          }
        ]
          ?.map((item: { key: string; label: string }) => {
            if (item?.key === 'utilization') {
              return `<li class="f-12">${item?.label}- <b>${
                retainer[item?.key]
              }%</b></li>`;
            }
            if (retainer?.offeringType === 'BLOCK_MONEY') {
              const currencyText = formatCurrency(retainer[item?.key]);
              return `<li class="f-12">${item?.label}- <b>${currencyText}</b></li>`;
            }
            return `<li class="f-12">${item?.label}- <b>${
              retainer[item?.key]
            }hr</b></li>`;
          })
          ?.join('');
        return `<div><div class="f-14 bold">${retainer?.client?.name}</div><ul class="m4 pl10">${tpvalue}</ul></div>`;
      }
    },
    xAxis: {
      data: chartData?.xAxis?.data,
      type: 'category',
      axisLabel: {
        interval: 0,
        rotate: 30,
        margin: 30,
        fontSize: 12,
        ellipsis: true,
        overflow: 'breakAll',
        width: 60,
        align: 'center'
      }
    },
    yAxis: {
      name: getTranslatedText(intl, 'invoice.retainerReport.usage.percentage'),
      nameLocation: 'end',
      nameTextStyle: {
        verticalAlign: 'bottom'
      },
      nameGap: 20
    },
    series: chartData?.series
  };

  return (
    <div className="full-width full-height position-relative">
      <div className="fx fw actr position-absolute z2">
        {/* <TextStyle variation="strong">
          <FormattedMessage
            id="common.label.contractsUtilization"
            defaultMessage="Top 10 Utilization contracts"
          />
        </TextStyle> */}
        <div className="mlauto w200">
          <MinimalDropDown
            options={usageOptions}
            onChange={handleUsageChange}
            value={usageOptions?.find((o) => o?.value === selectedUsage?.value)}
          />
        </div>
      </div>
      <ReactECharts
        option={option}
        style={{ height: 400 }}
        opts={{ renderer: 'svg' }}
      />
      {/* <RenderChart
        item={layout?.item}
        size={{
          width: layout?.size?.width - 40,
          height: layout?.size?.height - 100
        }}
        insightChartData={chartData}
        numberChartCallback={(data: any) => {
          setChartData(data);
        }}
        numberChartTooltip={getTranslatedText(intl, 'common.view.data')}
      /> */}
    </div>
  );
};

export default ContractsUtilizationChart;
